import * as React from 'react'

import { ResponsiveBar } from '@nivo/bar'
import { scaleOrdinal } from 'd3-scale'

import i18n from 'modules/core/lib/i18n'

import { formatterForGraph, validateFieldType } from 'modules/reports/lib/Constants'
import type ReportStruct from 'modules/reports/lib/ReportStruct'
import { UIColors, colorList } from 'modules/reports/lib/colors'
import nivoTheme from 'modules/reports/lib/nivoTheme'

interface Props {
  munched: any
  data: Array<Record<string, any>>
  reportStruct: ReportStruct
  showLegend: boolean
  chartData?: Record<string, any>
}

/**
 * BarChart uses the data structure from the Area muncher to render the chart.
 *
 * The Bar muncher create a data structure with these keys: indexBy, keys and processedData.
 * the indexBy contains the object keys to print the values of the X axis. then the keys key
 * contains an array of keys to group the processed data, and finally processedData contains
 * the data structure to print the chart.
 *
 * The Bar muncher internally generates an array of object that map the data into an array of
 * objects that use the keys of the X, Y axis and the groupBy to create a new array of objects,
 * These objects have their own keys set to the value of the X and Y axis or groupBy, with their
 * respective values also being used as the value for each key.
 *
 * @example
 * // {
 * //   indexBy: "cohort"
 * //   keys: ['count.leave.id'],
 * //   processedData:  [
 * //     {cohort: '2022-08-01', count.leave.id: 1}
 * //   ]
 * // }
 *
 * const data = {count.leave.id: 1, cohort: '2022-08-01'}
 * const aggregation = {
 *  function: "count",
 *  path: "leave.id"
 * }
 * const projection = {
 *  function: "count",
 *  path: "leave.id"
 * }
 *
 * const muncher = new Bar({data, aggregation, projection})
 * muncher.call()
 *
 * @constructor
 * @param munched - provide data structure output from the Bar muncher class
 * @param reportStruct - provide reportStruct in order to read the query projections
 * @param showLegend - provide showLegend in order to show the chart legend
 * @param chartData - provide chartData in order to read the sql projections
 */
const BarChart = ({ munched, reportStruct, showLegend, chartData }: Props) => {
  /**
   * formatBottomAxis - formats each value of the X axis with the projection type format,
   * the formatter is not available for sql.
   *
   * This function reads the projection type and returns a formatter that matches the
   * projection type.
   *
   * The projections available formatters are: number, identifier, boolean, count, date,
   * cents, money, cohort, link, year, month, unit, hours, enum
   *
   * @method
   * @example
   * // returns "3,00"
   * formatBottonAxis(3.0)
   **/
  const formatBottomAxis = (value: string) => {
    const projection = reportStruct.props.projections[1]

    if (projection && !validateFieldType(projection, value)) {
      return i18n.t('custom_reports.value_mismatch')
    }

    return projection && formatterForGraph(projection)(value)
  }

  /**
   * extraProps - returns the chart legends and its margin.
   * if showLegened is true and the reportStruct has projections available it will
   * return a hash in order to render the chart legends, however if there are not
   * projections available it return only the chart margins.
   */
  const extraProps = (): any => {
    const projections = reportStruct.props.projections

    if (showLegend && (projections.length > 2 || chartData?.tertiary_data)) {
      return {
        margin: {
          top: 30,
          right: 150,
          bottom: 60,
          left: 80,
        },
        legends: [
          {
            dataFrom: 'keys',
            anchor: 'bottom-right',
            direction: 'column',
            justify: false,
            translateX: 120,
            translateY: 0,
            itemsSpacing: 2,
            itemWidth: 100,
            itemHeight: 20,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolSize: 20,
          },
        ],
      }
    }

    return {
      margin: {
        top: 30,
        right: 50,
        bottom: 60,
        left: 80,
      },
    }
  }

  const scale = scaleOrdinal(colorList)

  if (!munched) return null
  const { processedData, keys, indexBy } = munched

  /**
   * addFormat - returns a formatter for the X axis if the reports was built with the report builder,
   * on the other hand it does not return a formatter.
   */
  const addFormat = () =>
    chartData
      ? null
      : {
          format: formatBottomAxis,
        }

  return (
    <ResponsiveBar
      enableGridY
      enableLabel
      data={processedData}
      label={(data: any) => data.value.toFixed(2)}
      colors={colorList}
      colorBy={(item: Record<string, any>) => {
        const value = item.id

        if (!value) return UIColors.grey3

        return scale(value)
      }}
      indexBy={indexBy}
      keys={keys}
      theme={nivoTheme}
      borderRadius={2}
      padding={0.3}
      groupMode="stacked"
      innerPadding={3}
      minValue="auto"
      maxValue="auto"
      axisBottom={{
        ...addFormat(),
        orient: 'bottom',
        tickSize: 5,
        tickPadding: 10,
        tickRotation: 20,
      }}
      labelSkipHeight={12}
      labelTextColor="inherit:darker(1.6)"
      {...extraProps()}
    />
  )
}

export default BarChart
